import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { RedirectPreserving, RedirectWithParam } from "./components/RedirectPreserving";
import Login from "./pages/Login";
import Welcome from "./pages/Welcome";
import WelcomeSuccess from "./pages/WelcomeSuccess";
import Onboarding from "./pages/Onboarding";

import SetPassword from "./pages/SetPassword";
import ResetPassword from "./pages/ResetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Dashboard from "./pages/Dashboard";
import NewDashboard from "./pages/NewDashboard";

import StrategyForm from "./pages/StrategyForm";
import StrategyComplete from "./pages/StrategyComplete";
import UnifiedTaskCenter from "./pages/UnifiedTaskCenter";
import Resources from "./pages/Resources";
import Support from "./pages/Support";
import ModuleView from "./pages/ModuleView";
import Community from "./pages/Community";
import PostDetail from "./pages/PostDetail";
import Affiliates from "./pages/Affiliates";
import NotFound from "@/pages/NotFound";
import CustomerJourney from "@/pages/CustomerJourney";
import MyJourney from "@/pages/MyJourney";
import StageDetail from "@/pages/StageDetail";
import TemplateDetail from "./pages/TemplateDetail";
import Settings from "./pages/Settings";
import UNAMembers from "./pages/UNAMembersUpdated";
import AcceptInvitation from "./pages/AcceptInvitation";
import TaskTemplates from "./pages/TaskTemplates";
import DocumentEditor from "./pages/DocumentEditor";
import CurrentEditionAdoption from "./pages/CurrentEditionAdoption";
import { ProtectedAdminRoute } from "./components/admin/ProtectedAdminRoute";

import { AdminLayout } from "./components/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminTemplateLibrary from "./pages/admin/AdminTemplateLibrary";
import WorkflowTemplateManager from "./pages/admin/WorkflowTemplateManager";
import WorkflowEditorPage from "./pages/admin/WorkflowEditorPage";
import WorkflowAnalytics from "./pages/admin/WorkflowAnalytics";
import AdminAccounts from "./pages/admin/AdminAccounts";
import TicketDetail from "./pages/admin/TicketDetail";
import AllUsers from "./pages/admin/AllUsers";
import MemberDetail from "./pages/admin/MemberDetail";
import AdminTaskManagement from "./pages/admin/AdminTaskManagement";
import SupportTickets from "./pages/admin/SupportTickets";
import SystemAuditLog from "./pages/admin/SystemAuditLog";
// Email wording consolidated into the Content editor.
import TemplateEditor from "./pages/admin/TemplateEditor";
import VersionHistory from "./pages/admin/VersionHistory";
import SignDocument from "./pages/SignDocument";
import WorkflowLibrary from "./pages/WorkflowLibrary";
import MainLayout from "./components/layout/MainLayout";
import AdminGovernanceHealth from "./pages/admin/AdminGovernanceHealth";
import AdminStewMirror from "./pages/admin/AdminStewMirror";
import AdminBriefingCorpus from "./pages/admin/AdminBriefingCorpus";
import StewForAgents from "./pages/admin/StewForAgents";
import StewImprovement from "./pages/admin/StewImprovement";
import ProspectConversations from "./pages/admin/ProspectConversations";
import AdminCallMining from "./pages/admin/AdminCallMining";
import AdminGatheringQuestions from "./pages/admin/AdminGatheringQuestions";
import StewWidget from "./pages/StewWidget";
import GovernanceDocuments from "./pages/GovernanceDocuments";
import GovernanceDocumentDetail from "./pages/GovernanceDocumentDetail";
import UNAGovernance from "./pages/UNAGovernance";
import UNAConfirmation from "./pages/UNAConfirmation";
import JourneyStart from "./pages/JourneyStart";
import Governance from "./pages/Governance";
import FormACompany from "./pages/FormACompany";
import LibraryDocument from "./pages/LibraryDocument";
import ManualAdmin from "./pages/admin/ManualAdmin";
import ManualAdminEdit from "./pages/admin/ManualAdminEdit";
import DataExport from "./pages/admin/DataExport";
import StorageCleanup from "./pages/admin/StorageCleanup";
import CanonRegister from "./pages/admin/CanonRegister";
import StewBrain from "./pages/admin/StewBrain";
import NameReview from "./pages/admin/NameReview";
import Fulfillment from "./pages/admin/Fulfillment";
import Messages from "./pages/admin/Messages";
import ContentBlocksAdmin from "./pages/admin/ContentBlocks";
import EinCalendar from "./pages/admin/EinCalendar";
import OnboardingBoard from "./pages/admin/OnboardingBoard";
import AdminMemberRecord from "./pages/admin/AdminMemberRecord";
import FormationReview from "./pages/admin/FormationReview";
import Tracking from "./pages/Tracking";
import MemberRecord from "./pages/MemberRecord";
import QrLanding from "./pages/QrLanding";
import SuiteRegistry from "./pages/admin/SuiteRegistry";
import LegacyImport from "./pages/admin/LegacyImport";
import DocumentJobs from "./pages/admin/DocumentJobs";
import AdminAdoptionDashboard from "./pages/admin/AdminAdoptionDashboard";
import AdminAdoptionReview from "./pages/admin/AdminAdoptionReview";

import CanonSectionWorkspace from "./pages/admin/CanonSectionWorkspace";
import CanonExport from "./pages/admin/CanonExport";
import AdminLinkHealth from "./pages/admin/AdminLinkHealth";
import ErrorRegister from "./pages/admin/ErrorRegister";
import AdminFeedback from "./pages/admin/AdminFeedback";
import { JourneyRouteGuard } from "./components/JourneyRouteGuard";
import { FeedbackButton } from "./components/feedback/FeedbackButton";
import { OfflineBanner } from "./components/OfflineBanner";
import Succession from "./pages/Succession";
import SuccessionDecisions from "./pages/SuccessionDecisions";
import SuccessionDocuments from "./pages/SuccessionDocuments";
import SuccessionExecution from "./pages/SuccessionExecution";
import SuccessionRhythm from "./pages/SuccessionRhythm";
import SuccessionOverview from "./pages/SuccessionOverview";
import SuccessorLanding from "./pages/SuccessorLanding";
import GovernanceOffices from "./pages/GovernanceOffices";
import OfficerCertificates from "./pages/OfficerCertificates";
import { WorkflowsRoute } from "./components/WorkflowsRoute";
import { ProtectedReviewerRoute } from "./components/review/ProtectedReviewerRoute";
import ReviewerWorkbench from "./pages/review/ReviewerWorkbench";
import MyQuestions from "./pages/MyQuestions";
import MyQuestion from "./pages/MyQuestion";
import StewStandalone from "./pages/StewStandalone";
import MemberApplications from "./pages/MemberApplications";
import PublicApplication from "./pages/PublicApplication";

const App = () => (
  <BrowserRouter>
    <AuthProvider>

      <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Navigate to="/login" replace />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/welcome/success" element={<ProtectedRoute><WelcomeSuccess /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/tracking" element={<ProtectedRoute><Tracking /></ProtectedRoute>} />
            <Route path="/record" element={<ProtectedRoute><MemberRecord /></ProtectedRoute>} />
            <Route
              path="/governance/current-edition"
              element={<ProtectedRoute><CurrentEditionAdoption /></ProtectedRoute>}
            />

            <Route path="/j/:shortcode" element={<QrLanding />} />

            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/stew" element={<ProtectedRoute><StewStandalone /></ProtectedRoute>} />
            <Route path="/stew-widget" element={<StewWidget />} />
            <Route path="/confirm-una" element={<ProtectedRoute><UNAConfirmation /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><NewDashboard /></ProtectedRoute>} />
            <Route path="/dashboard/old" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

            <Route path="/strategy-form" element={<ProtectedRoute><StrategyForm /></ProtectedRoute>} />
            <Route path="/strategy-complete" element={<ProtectedRoute><StrategyComplete /></ProtectedRoute>} />
            <Route path="/tasks/templates" element={<ProtectedRoute><TaskTemplates /></ProtectedRoute>} />
            <Route path="/tasks/:taskId/documents/:documentId/edit" element={<ProtectedRoute><DocumentEditor /></ProtectedRoute>} />
            <Route path="/resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
            <Route path="/resources/samples/:templateId" element={<ProtectedRoute><TemplateDetail /></ProtectedRoute>} />

            {/* Support hub: child paths per tab; bare /support renders the default (stew) */}
            <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
            <Route path="/support/stew" element={<ProtectedRoute><Support /></ProtectedRoute>} />
            {/* Members type this straight into the bar. Send them to Stew. */}
            <Route path="/stew" element={<RedirectPreserving to="/support/stew" />} />
            <Route path="/support/library" element={<ProtectedRoute><Support /></ProtectedRoute>} />
            <Route path="/support/ask" element={<ProtectedRoute><Support /></ProtectedRoute>} />

            {/* Unified library documents (both collections + dictionary) */}
            <Route path="/support/library/dictionary" element={<ProtectedRoute><LibraryDocument /></ProtectedRoute>} />
            {/* Renamed slugs: keep old addresses working */}
            <Route
              path="/support/library/how-would-this-ever-hold-up-the-honest-answer-to-the-defensi"
              element={<RedirectPreserving to="/support/library/how-would-this-ever-hold-up" />}
            />
            <Route
              path="/support/library/appendix"
              element={<RedirectPreserving to="/support/library/professional-briefing" />}
            />
            <Route path="/support/library/:slug" element={<ProtectedRoute><LibraryDocument /></ProtectedRoute>} />


            {/* Member questions */}
            <Route path="/support/questions" element={<ProtectedRoute><MyQuestions /></ProtectedRoute>} />
            <Route path="/support/questions/:id" element={<ProtectedRoute><MyQuestion /></ProtectedRoute>} />

            <Route path="/learning/course/:slug/module/:moduleNumber" element={<ProtectedRoute><ModuleView /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/una-governance" element={<ProtectedRoute><JourneyRouteGuard><UNAGovernance /></JourneyRouteGuard></ProtectedRoute>} />
            <Route path="/una-members" element={<ProtectedRoute><JourneyRouteGuard><UNAMembers /></JourneyRouteGuard></ProtectedRoute>} />
            <Route path="/governance-documents" element={<ProtectedRoute><JourneyRouteGuard><GovernanceDocuments /></JourneyRouteGuard></ProtectedRoute>} />
            <Route path="/governance-documents/:documentId" element={<ProtectedRoute><JourneyRouteGuard><GovernanceDocumentDetail /></JourneyRouteGuard></ProtectedRoute>} />

            {/* Governance hub: child paths per tab; bare /governance renders default (tools) */}
            <Route path="/governance" element={<ProtectedRoute><Governance /></ProtectedRoute>} />
            <Route path="/governance/tools" element={<ProtectedRoute><Governance /></ProtectedRoute>} />
            <Route path="/governance/health" element={<ProtectedRoute><Governance /></ProtectedRoute>} />
            <Route path="/governance/tasks" element={<ProtectedRoute><Governance /></ProtectedRoute>} />
            <Route path="/governance/record" element={<ProtectedRoute><Governance /></ProtectedRoute>} />
            <Route path="/governance/form-a-company" element={<ProtectedRoute><FormACompany /></ProtectedRoute>} />
            <Route path="/governance/certificates" element={<ProtectedRoute><OfficerCertificates /></ProtectedRoute>} />

            <Route path="/apply/:token" element={<PublicApplication />} />
            <Route path="/una-members/applications" element={<ProtectedRoute><MemberApplications /></ProtectedRoute>} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/sign-document" element={<SignDocument />} />
            <Route path="/succession" element={<ProtectedRoute><Succession /></ProtectedRoute>} />
            <Route path="/succession/decisions" element={<ProtectedRoute><SuccessionDecisions /></ProtectedRoute>} />
            <Route path="/succession/documents" element={<ProtectedRoute><SuccessionDocuments /></ProtectedRoute>} />
            <Route path="/succession/execution" element={<ProtectedRoute><SuccessionExecution /></ProtectedRoute>} />
            <Route path="/succession/rhythm" element={<ProtectedRoute><SuccessionRhythm /></ProtectedRoute>} />
            <Route path="/succession/overview" element={<ProtectedRoute><SuccessionOverview /></ProtectedRoute>} />
            <Route path="/governance/offices" element={<ProtectedRoute><GovernanceOffices /></ProtectedRoute>} />

            <Route path="/successor" element={<ProtectedRoute><SuccessorLanding /></ProtectedRoute>} />

            {/* ---- Legacy redirects (preserve query + hash) ---- */}
            <Route path="/my-tasks" element={<RedirectPreserving to="/governance/tasks" />} />
            <Route path="/member-dashboard" element={<Navigate to="/governance/tasks?view=assigned" replace />} />
            <Route path="/my-assigned-tasks" element={<Navigate to="/governance/tasks?view=assigned" replace />} />
            <Route path="/governance-health" element={<RedirectPreserving to="/governance/health" />} />
            <Route path="/vault" element={<RedirectPreserving to="/governance/record" />} />
            <Route path="/knowledge-base" element={<RedirectPreserving to="/support/library" />} />
            <Route path="/my-questions" element={<RedirectPreserving to="/support/questions" />} />
            <Route path="/my-questions/:id" element={<RedirectWithParam basePath="/support/questions" param="id" />} />
            <Route path="/manual" element={<RedirectPreserving to="/support/library" />} />
            <Route path="/help/dictionary" element={<RedirectPreserving to="/support/library/dictionary" />} />
            <Route path="/help/:slug" element={<RedirectWithParam basePath="/support/library" param="slug" />} />
            <Route path="/manual/:slug" element={<RedirectWithParam basePath="/support/library" param="slug" />} />
            <Route path="/resources/template/:templateId" element={<RedirectWithParam basePath="/resources/samples" param="templateId" />} />

            {/* Workflows */}
            <Route
              path="/workflows"
              element={
                <ProtectedRoute>
                  <JourneyRouteGuard>
                    <WorkflowsRoute>
                      <MainLayout>
                        <WorkflowLibrary />
                      </MainLayout>
                    </WorkflowsRoute>
                  </JourneyRouteGuard>
                </ProtectedRoute>
              }
            />

            {/* Journey */}
            <Route path="/journey/start" element={<JourneyStart />} />
            <Route path="/journey" element={<ProtectedRoute><MyJourney /></ProtectedRoute>} />
            <Route path="/journey/old" element={<ProtectedRoute><CustomerJourney /></ProtectedRoute>} />
            <Route path="/journey/stage/:stageType" element={<ProtectedRoute><StageDetail /></ProtectedRoute>} />

            {/* Reviewer Workbench */}
            <Route path="/review" element={<ProtectedReviewerRoute><ReviewerWorkbench /></ProtectedReviewerRoute>} />
            <Route path="/revew" element={<RedirectPreserving to="/review" />} />

            {/* Legacy admin redirects (must precede /admin/* Outlet routes) */}
            <Route path="/manual/admin" element={<RedirectPreserving to="/admin/library" />} />
            <Route path="/manual/admin/:docId" element={<RedirectWithParam basePath="/admin/library" param="docId" />} />
            <Route path="/admin/manual" element={<RedirectPreserving to="/admin/library" />} />
            <Route path="/admin/manual/:docId" element={<RedirectWithParam basePath="/admin/library" param="docId" />} />
            {/* Retired or renamed admin addresses that people still click or
                bookmark. Each lands on the surface that owns that work now. */}
            <Route path="/admin/ai/chats" element={<RedirectPreserving to="/admin/prospect-conversations" />} />
            <Route path="/admin/ai/settings" element={<RedirectPreserving to="/admin/stew-improvement" />} />
            <Route path="/admin/review-progress" element={<RedirectPreserving to="/admin/library" />} />
            <Route path="/admin/users/subscriptions" element={<RedirectPreserving to="/admin/users" />} />
            <Route path="/admin/system/features" element={<RedirectPreserving to="/admin/dashboard" />} />
            <Route path="/admin/system/governance/health" element={<RedirectPreserving to="/admin/system/governance-health" />} />
            <Route path="/admin/analytics/revenue" element={<RedirectPreserving to="/admin/dashboard" />} />
            <Route path="/admin/analytics/usage" element={<RedirectPreserving to="/admin/dashboard" />} />
            <Route path="/admin/support/faqs" element={<RedirectPreserving to="/admin/library" />} />


            {/* Admin */}
            <Route path="/admin" element={<ProtectedAdminRoute><AdminLayout /></ProtectedAdminRoute>}>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="templates" element={<AdminTemplateLibrary />} />
              <Route path="templates/documents" element={<AdminTemplateLibrary />} />
              <Route path="templates/workflows" element={<WorkflowTemplateManager />} />
              <Route path="templates/workflows/:id/edit" element={<WorkflowEditorPage />} />
              <Route path="templates/workflows/:id/analytics" element={<WorkflowAnalytics />} />
              <Route path="templates/new" element={<TemplateEditor />} />
              <Route path="templates/:id/edit" element={<TemplateEditor />} />
              <Route path="templates/:id/history" element={<VersionHistory />} />
              <Route path="users" element={<AllUsers />} />
              <Route path="users/:userId" element={<MemberDetail />} />
              <Route path="users/tasks" element={<AdminTaskManagement />} />
              <Route path="users/admins" element={<AdminAccounts />} />
              <Route path="support/tickets" element={<SupportTickets />} />
              <Route path="support/tickets/:ticketId" element={<TicketDetail />} />
              <Route path="support/feedback" element={<AdminFeedback />} />
              <Route path="system/audit" element={<SystemAuditLog />} />
              <Route path="system/emails" element={<ContentBlocksAdmin />} />
              <Route path="system/governance-health" element={<AdminGovernanceHealth />} />
              <Route path="system/stew-mirror" element={<AdminStewMirror />} />
              <Route path="briefing-corpus" element={<AdminBriefingCorpus />} />
              <Route path="stew" element={<StewForAgents />} />
              <Route path="stew-improvement" element={<StewImprovement />} />
              <Route path="prospect-conversations" element={<ProspectConversations />} />
              <Route path="call-mining" element={<AdminCallMining />} />
              <Route path="gathering-questions" element={<AdminGatheringQuestions />} />
              <Route path="system/export" element={<DataExport />} />
              <Route path="system/errors" element={<ErrorRegister />} />
              <Route path="system/storage" element={<StorageCleanup />} />
              <Route path="names" element={<NameReview />} />
              <Route path="suites" element={<SuiteRegistry />} />
              <Route path="import-legacy" element={<LegacyImport />} />
              <Route path="docgen" element={<DocumentJobs />} />
              <Route path="adoptions" element={<AdminAdoptionDashboard />} />
              <Route path="adoptions/:id" element={<AdminAdoptionReview />} />

              <Route path="onboarding" element={<OnboardingBoard />} />
              <Route path="fulfillment" element={<Fulfillment />} />
              <Route path="review" element={<FormationReview />} />
              <Route path="messages" element={<Messages />} />
              <Route path="content" element={<ContentBlocksAdmin />} />
              <Route path="ein-calendar" element={<EinCalendar />} />
              <Route path="members/:id" element={<AdminMemberRecord />} />
              <Route path="canon" element={<CanonRegister />} />
              <Route path="canon-export" element={<CanonExport />} />
              <Route path="stew-brain" element={<StewBrain />} />
              <Route path="canon/:section" element={<CanonSectionWorkspace />} />
              <Route path="error-register" element={<RedirectPreserving to="/admin/system/errors" />} />
              <Route path="library" element={<ManualAdmin />} />
              <Route path="library/:docId" element={<ManualAdminEdit />} />
              <Route path="link-health" element={<AdminLinkHealth />} />
              <Route index element={<AdminDashboard />} />
            </Route>

            <Route path="*" element={<NotFound />} />
      </Routes>
      <FeedbackButton />
      <OfflineBanner />

    </AuthProvider>
  </BrowserRouter>
);

export default App;
